import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { SignInProvider } from "../shell/provider";

/** The provider this deployment names, or undefined while it is being asked
 * and wherever it names none (#820).
 *
 * **It is here rather than in `Login` because three anonymous pages ask it** —
 * the sign-in page, the invitation's acceptance (#506) and an identity link's
 * page (#1034) — and two copies of a read is how two pages come to disagree
 * about whether a door exists. */
export function useProviderDoor(): SignInProvider | undefined {
  return useSignInDoors().provider;
}

/** The provider door and whether this host asks for an address before it
 * (ADR-0183 decision 2f): `discovery` is true on the neutral home only.
 *
 * `loaded` is whether the deployment has answered. Until it has, an absent
 * `provider` says nothing; once it has, an absent `provider` is a deployment
 * that names none — the one where an invitation is accepted with a password
 * set on this console (ADR-0466), so the acceptance page must not draw that
 * door on a question still in flight. A read that failed counts as loaded:
 * the acceptance's own first call is what decides the door, and a page that
 * guessed wrong is told so by it. `answered` is narrower — the deployment
 * actually said what its doors are — for copy that must not describe the
 * password door on a read that failed.
 *
 * `passwordReset` is whether a forgotten password is reset by a mailed link
 * here (ADR-0466 decision 4b), about the deployment and nobody else. */
export function useSignInDoors(): { provider?: SignInProvider; discovery: boolean; loaded: boolean; answered: boolean; passwordReset: boolean } {
  const [doors, setDoors] = useState<{ provider?: SignInProvider; discovery: boolean; loaded: boolean; answered: boolean; passwordReset: boolean }>({ discovery: false, loaded: false, answered: false, passwordReset: false });
  useEffect(() => {
    let live = true;
    void api.GET("/auth/doors").then(({ data }) => {
      if (live) setDoors({ provider: data?.provider, discovery: !!data?.provider && data?.discovery === true, loaded: true, answered: data !== undefined, passwordReset: data?.password_reset === true });
    });
    return () => { live = false; };
  }, []);
  return doors;
}

/** The door the address names: the one pinned to the organization hosting
 * that person, or this host's ordinary one for every other address (ADR-0183
 * decision 2f) — each carrying the address back as `login_hint`, so the
 * provider does not ask for it again (ADR-0191). The address goes in the
 * body, never in the URL Zenith is sent. Undefined where the answer names no
 * provider, which the caller treats as an outage. */
export async function discoverDoor(email: string): Promise<SignInProvider | undefined> {
  const { data } = await api.POST("/auth/doors:discover", { body: { email } });
  return data?.provider;
}
